import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Slider from "react-slick";
import {
  FaCalendarAlt,
  FaClock,
  FaTaxi,
  FaShieldAlt,
  FaUserTie,
  FaRupeeSign,
  FaPhoneAlt,
  FaPlus,
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaRoute,
  FaArrowRight,
  FaPlane,
  FaCheckCircle,
  FaThumbsUp,
} from "react-icons/fa";
import axios from "axios";

// Import background image
import cabBgImage from "../assets/images/cab-bg.png";
import banglor from "../assets//images/q.png";
import shimla from "../assets/images/s.jpeg";
import haridwar from "../assets/images/a.jpeg";
import delhi from "../assets/images/d.jpeg";

// Import experience images
import image1 from "../assets/images/1.jpeg";
import image2 from "../assets/images/2.jpeg";
import image3 from "../assets/images/3.jpeg";
import image4 from "../assets/images/4.jpeg";
//car images
import car1 from "../assets/images/car-1.jpeg";
import car2 from "../assets/images/car-2.jpeg";
//taxi images
import taxi from "../assets/images/taxi-1.jpeg";
import taxi2 from "../assets/images/taxi-2.jpeg";

// Import testimonial images
import testi1 from "../assets/images/test-1.jpeg";
import testi2 from "../assets/images/test-2.jpeg";
import testi3 from "../assets/images/test-3.jpeg";
import image5 from "../assets/images/5.jpeg";
import image6 from "../assets/images/6.jpeg";
import image7 from "../assets/images/7.jpeg";
import image8 from "../assets/images/8.jpeg";

// Custom components
import LocationInput from "../components/LocationInput";

// Components and Redux actions
import { updateBookingForm } from "../features/booking/bookingSlice";
import { API_URL, FALLBACK_API_URL } from "../config/apiConfig";

// Use a fallback image URL directly since dynamic imports cause ESLint issues
const outstationTaxiImage =
  "https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";

// Google Maps API is now handled by GoogleMapsProvider

// Known distances between major cities (in km)
const KNOWN_DISTANCES = {
  "rajkot-delhi": 1140,
  "delhi-rajkot": 1140,
  "mumbai-pune": 150,
  "pune-mumbai": 150,
  "bangalore-chennai": 350,
  "chennai-bangalore": 350,
  // Add more known routes as needed
};

// Dynamic data will be fetched from admin panel

// Normalize city name for consistent comparison
const normalizeCityName = (city) => {
  if (!city) return "";
  return city.toString().toLowerCase().trim();
};

// Get known distance between two cities if available
const getKnownDistance = (from, to) => {
  const fromNormalized = normalizeCityName(from);
  const toNormalized = normalizeCityName(to);
  const cacheKey = `${fromNormalized}-${toNormalized}`;
  return KNOWN_DISTANCES[cacheKey] || null;
};

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get user from Redux store
  const { user } = useSelector((state) => state.auth);

  // Form state
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [journeyType, setJourneyType] = useState("outstation"); // outstation, local
  const [tripType, setTripType] = useState("oneWay"); // oneWay, roundTrip
  const [localServiceType, setLocalServiceType] = useState("rental"); // rental, airport
  const [multiCity, setMultiCity] = useState(false);

  // Local Rental specific state
  const [localCity, setLocalCity] = useState("");
  const [hourlyPackage, setHourlyPackage] = useState("2h/20km");

  // Airport Transfer specific state
  const [transferDirection, setTransferDirection] = useState("to"); // "to" or "from"
  const [selectedAirport, setSelectedAirport] = useState("");
  const [airportCity, setAirportCity] = useState("");

  // Dynamic data from admin panel
  const [rentalPackages, setRentalPackages] = useState([]);
  const [airports, setAirports] = useState([]);
  const [airportServiceCities, setAirportServiceCities] = useState([]);
  const [adminCities, setAdminCities] = useState([]);


  const [pickupDate, setPickupDate] = useState(new Date());
  // Get current time in 24-hour format (HH:MM)
  const getCurrentTime = useCallback(() => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const mins = now.getMinutes().toString().padStart(2, "0");
    return `${hours}:${mins}`;
  }, []);

  // Format time to 12-hour format with AM/PM
  const formatTo12Hour = useCallback((time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  }, []);

  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = useCallback((time12) => {
    if (!time12) return "";
    const [time, period] = time12.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (period === "PM" && hours < 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }, []);
  // Initialize pickup time with current time in 12-hour format
  const [pickupTime, setPickupTime] = useState(() => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();

    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  });
  const [returnDate, setReturnDate] = useState(
    new Date(new Date().setDate(new Date().getDate() + 1)),
  );
  // Initialize return time with a sensible default in 12-hour format
  const [returnTime, setReturnTime] = useState("10:00 AM");
  const [mobileNumber, setMobileNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Additional cities for multi-city bookings
  const [additionalCities, setAdditionalCities] = useState([]);
  const [cityCounter, setCityCounter] = useState(1);

  // API data
  const [popularCities, setPopularCities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactHumanVerification, setContactHumanVerification] =
    useState(false);

  // Business form state
  const [businessForm, setBusinessForm] = useState({
    companyName: "",
    name: "",
    email: "",
    phone: "",
  });
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessHumanVerification, setBusinessHumanVerification] =
    useState(false);

  // Slider settings for popular cities
  const citySettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const images = [
    image1,
    image2,
    image3,
    image4,
    image5,
    image6,
    image7,
    image8,
  ];

  // Experience data with proper names and descriptions
  const experiences = [
    {
      image: image1,
      title: "Mountain Escapes",
      description: "Scenic hill station getaways with breathtaking views"
    },
    {
      image: image2,
      title: "Beach Destinations",
      description: "Coastal paradise trips with sun, sand and sea"
    },
    {
      image: image3,
      title: "Heritage Tours",
      description: "Explore ancient monuments and cultural landmarks"
    },
    {
      image: image4,
      title: "City Exploration",
      description: "Discover vibrant urban attractions and nightlife"
    },
    {
      image: image5,
      title: "Wildlife Safari",
      description: "Adventure through national parks and sanctuaries"
    },
    {
      image: image6,
      title: "Spiritual Journeys",
      description: "Visit sacred temples and pilgrimage destinations"
    },
    {
      image: image7,
      title: "Desert Adventures",
      description: "Experience the charm of golden sand dunes"
    },
    {
      image: image8,
      title: "Valley Retreats",
      description: "Peaceful getaways in lush green valleys"
    }
  ];

  // Fetch dynamic data from admin panel
  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        // Fetch rental packages
        try {
          console.log("🎁 Attempting to fetch rental packages...");
          const rentalResponse = await tryApiRequest(
            "/rental-package",
            null,
            "Fetching rental packages",
            "GET",
          );
          console.log("🎁 Rental packages API response:", rentalResponse?.data);
          if (rentalResponse?.data?.success) {
            console.log(
              "🎁 Setting rental packages:",
              rentalResponse.data.data,
            );
            console.log(
              "🎁 Number of packages:",
              rentalResponse.data.data.length,
            );
            setRentalPackages(rentalResponse.data.data);
          } else {
            console.log(
              "🎁 Rental packages API not successful:",
              rentalResponse?.data,
            );
          }
        } catch (error) {
          console.error("🎁 Failed to fetch rental packages:", error.message);
        }

        // Fetch airports
        try {
          const airportResponse = await tryApiRequest(
            "/airport",
            null,
            "Fetching airports",
            "GET",
          );
          if (airportResponse?.data?.success) {
            setAirports(airportResponse.data.data);
          }
        } catch (error) {
          console.error("Failed to fetch airports:", error.message);
        }

        // Skip fetching cities - we get them from rental packages instead
        console.log(
          "🏙️ Skipping AdminCities fetch - using rental package cities instead",
        );
      } catch (error) {
        console.error("Error in fetchAdminData:", error);
      }
    };

    fetchAdminData();
  }, []);

  // Prefill mobile number if user is logged in
  useEffect(() => {
    // Check for user phone number - this runs on component mount and when Redux user changes
    const prefillMobileNumber = () => {
      console.log("Checking for user phone number to prefill");

      // First priority: Direct user object in localStorage - this is what authSlice.js uses
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);
          if (userData) {
            console.log("Found user data in localStorage:", userData);
            if (userData.phone) {
              console.log(
                "Setting mobile from localStorage user.phone:",
                userData.phone,
              );
              setMobileNumber(userData.phone);
              return true;
            } else if (userData.mobile) {
              console.log(
                "Setting mobile from localStorage user.mobile:",
                userData.mobile,
              );
              setMobileNumber(userData.mobile);
              return true;
            } else if (userData.user && userData.user.phone) {
              console.log(
                "Setting mobile from localStorage user.user.phone:",
                userData.user.phone,
              );
              setMobileNumber(userData.user.phone);
              return true;
            }
          }
        }
      } catch (e) {
        console.error("Error parsing user from localStorage:", e);
      }

      // Second priority: Redux store (should match localStorage in most cases)
      if (user) {
        console.log("Checking Redux user:", user);
        if (user.phone) {
          console.log("Setting mobile from Redux user.phone:", user.phone);
          setMobileNumber(user.phone);
          return true;
        } else if (user.mobile) {
          console.log("Setting mobile from Redux user.mobile:", user.mobile);
          setMobileNumber(user.mobile);
          return true;
        } else if (user.user && user.user.phone) {
          console.log(
            "Setting mobile from Redux user.user.phone:",
            user.user.phone,
          );
          setMobileNumber(user.user.phone);
          return true;
        }
      }

      // Last resort - userInfo in localStorage (not used by auth system but might be used elsewhere)
      try {
        const userInfoStr = localStorage.getItem("userInfo");
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo) {
            console.log("Found userInfo in localStorage:", userInfo);
            if (userInfo.phone) {
              console.log(
                "Setting mobile from localStorage userInfo.phone:",
                userInfo.phone,
              );
              setMobileNumber(userInfo.phone);
              return true;
            } else if (userInfo.mobile) {
              console.log(
                "Setting mobile from localStorage userInfo.mobile:",
                userInfo.mobile,
              );
              setMobileNumber(userInfo.mobile);
              return true;
            }
          }
        }
      } catch (e) {
        console.error("Error parsing userInfo from localStorage:", e);
      }

      console.log("No user phone number found in any storage location");
      return false;
    };

    // Run prefill on component mount
    prefillMobileNumber();
  }, [user]); // This effect will re-run when the Redux user changes

  // Fetch service cities when airport is selected
  useEffect(() => {
    const fetchServiceCities = async () => {
      if (selectedAirport) {
        try {
          const response = await tryApiRequest(
            `/airport/${selectedAirport}/cities`,
            null,
            `Fetching service cities for ${selectedAirport}`,
            "GET",
          );
          if (response?.data?.success) {
            setAirportServiceCities(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching service cities:", error);
          setAirportServiceCities([]);
        }
      } else {
        setAirportServiceCities([]);
      }
    };

    fetchServiceCities();
  }, [selectedAirport]);

  // Handle URL parameters for auto-filling locations (from route redirects)
  useEffect(() => {
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");
    const tripParam = searchParams.get("trip");

    if (fromParam) {
      setPickup(fromParam);
      console.log("Auto-filled pickup location from URL:", fromParam);
    }

    if (toParam) {
      setDropoff(toParam);
      console.log("Auto-filled dropoff location from URL:", toParam);
    }

    if (tripParam === "oneWay") {
      setTripType("oneWay");
      setJourneyType("outstation");
      console.log("Set journey to outstation one-way from URL");
    }

    // Clear URL parameters after processing to clean up the URL
    if (fromParam || toParam || tripParam) {
      const newUrl = window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }
  }, [searchParams]);

  // Fetch data from backend
  useEffect(() => {
    const fetchPopularCities = async () => {
      setIsLoading(true);
      try {
        console.log(
          "Home: Fetching popular cities from:",
          `${API_URL}/popular-cities`,
        );
        const response = await axios.get(`${API_URL}/popular-cities`);
        console.log("Home: Popular cities response:", response.data);

        if (response.data && Array.isArray(response.data)) {
          setPopularCities(response.data);
        } else if (response.data && response.data.success) {
          setPopularCities(response.data.cities || []);
        }
      } catch (error) {
        console.error("Error fetching popular cities:", error);
        setPopularCities([]);
        // Don't show error to user for optional data
        console.warn(
          "Popular cities could not be loaded, continuing without them",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPopularCities();
  }, [API_URL]);

  // Helper function to check if a date is today
  const isToday = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // Update pickup time every minute to ensure real-time options
  useEffect(() => {
    const timer = setInterval(() => {
      if (isToday(pickupDate)) {
        const currentTime = getCurrentTime();
        const [hours, minutes] = currentTime.split(":").map(Number);
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        setPickupTime(
          `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`,
        );
      }
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [pickupDate, getCurrentTime, isToday]);

  // Handle pickup time change
  const handlePickupTimeChange = (e) => {
    const newTime = e.target.value;
    if (isToday(pickupDate)) {
      const currentTime = getCurrentTime();
      const [currentHours, currentMinutes] = currentTime.split(":").map(Number);
      const [newHours, newMinutes] = convertTo24Hour(newTime)
        .split(":")
        .map(Number);
      const currentTotalMinutes = currentHours * 60 + currentMinutes;
      const newTotalMinutes = newHours * 60 + newMinutes;
      if (newTotalMinutes < currentTotalMinutes) {
        const ampm = currentHours >= 12 ? "PM" : "AM";
        const displayHours = currentHours % 12 || 12;
        setPickupTime(
          `${displayHours}:${String(currentMinutes).padStart(2, "0")} ${ampm}`,
        );
        toast.warn("Pickup time cannot be earlier than the current time", {
          autoClose: 3000,
        });
        return;
      }
    }
    setPickupTime(newTime);
  };

  // Validate if a time is in the past for the current date
  const validateTime = useCallback(
    (date, time12) => {
      if (!time12) return false;

      // If not today, any time is valid
      if (!isToday(date)) return true;

      // Get current time in 24-hour format
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      // Convert selected time to 24-hour format for comparison
      const selectedTime24 = convertTo24Hour(time12);
      if (!selectedTime24) return false;

      const [selectedHours, selectedMinutes] = selectedTime24
        .split(":")
        .map(Number);

      // Compare hours and minutes
      if (selectedHours > currentHours) return true;
      if (selectedHours === currentHours) {
        return selectedMinutes >= currentMinutes;
      }
      return false;
    },
    [isToday, convertTo24Hour],
  );

  // Get the earliest allowed time for the time picker
  const getEarliestAllowedTime = useCallback(() => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();

    // Round up to next 5 minutes
    if (minutes % 5 !== 0) {
      minutes = minutes + (5 - (minutes % 5));
      if (minutes >= 60) {
        hours += 1;
        minutes = 0;
      }
    }

    // Handle midnight rollover
    if (hours >= 24) {
      hours = 0;
    }

    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  }, []);

  // Handle time change with validation
  const handleTimeChange = useCallback(
    (newTime, setTime) => {
      if (!newTime) return;

      // Always update the time first to ensure UI is responsive
      setTime(newTime);

      if (isToday(pickupDate)) {
        const isValid = validateTime(pickupDate, newTime);
        if (!isValid) {
          const earliestTime = getEarliestAllowedTime();
          // Use setTimeout to ensure state update happens after the current render cycle
          setTimeout(() => {
            setTime(earliestTime);
            toast.warn(
              "Selected time is in the past. Adjusted to the earliest available slot.",
            );
          }, 0);
        }
      }
    },
    [pickupDate, isToday, validateTime, getEarliestAllowedTime],
  );

  // Get Google Maps API status and methods from context
  const {
    isLoaded: isGoogleMapsReady,
    calculateDistance,
    loadError,
  } = useGoogleMaps();

  // Handle Google Maps load errors
  useEffect(() => {
    if (loadError) {
      console.error("Error loading Google Maps API:", loadError);
      toast.error("Error loading maps. Please refresh the page.");
    }
  }, [loadError]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🚀 Form submission started");
    console.log("📊 Current form state:", {
      journeyType,
      tripType,
      localServiceType,
      pickup,
      dropoff,
      localCity,
      hourlyPackage,
      selectedAirport,
      mobileNumber,
      errorMessage,
    });

    // Clear previous errors
    setErrorMessage("");

    // Basic validation based on journey type
    if (journeyType === "outstation") {
      console.log("✅ Validating outstation journey");
      if (!pickup || !dropoff) {
        console.log("❌ Missing pickup or dropoff locations");
        setErrorMessage("Please enter both pickup and drop-off locations");
        return;
      }
      // Check if pickup and dropoff are the same
      if (pickup.toLowerCase() === dropoff.toLowerCase()) {
        console.log("❌ Pickup and dropoff are the same");
        setErrorMessage("Pickup and drop-off locations cannot be the same");
        return;
      }
      console.log("✅ Outstation validation passed");
    } else if (journeyType === "local") {
      console.log("✅ Validating local journey");
      if (localServiceType === "rental") {
        console.log("✅ Validating rental service");
        console.log("📊 Rental validation data:", {
          localCity,
          hourlyPackage,
          rentalPackagesLength: rentalPackages.length,
          availableCities: rentalPackages
            .filter((pkg) => pkg.serviceCities && pkg.serviceCities.length > 0)
            .flatMap((pkg) =>
              pkg.serviceCities.map((sc) => sc.cityName || sc.name || sc),
            )
            .filter(Boolean),
        });

        if (!localCity) {
          console.log("❌ Missing local city, current value:", localCity);
          setErrorMessage("Please select a city for local rental");
          return;
        }
        if (!hourlyPackage) {
          console.log(
            "❌ Missing hourly package, current value:",
            hourlyPackage,
          );
          setErrorMessage("Please select an hourly package");
          return;
        }
        console.log("✅ Rental validation passed");
      } else if (localServiceType === "airport") {
        console.log("✅ Validating airport service");
        if (!selectedAirport) {
          console.log("❌ Missing selected airport");
          setErrorMessage("Please select an airport");
          return;
        }
        if (transferDirection === "to" && !pickup) {
          console.log("❌ Missing pickup city for airport transfer");
          setErrorMessage("Please select pickup city");
          return;
        }
        if (transferDirection === "from" && !dropoff) {
          console.log("❌ Missing drop city for airport transfer");
          setErrorMessage("Please select drop city");
          return;
        }
        console.log("✅ Airport validation passed");
      }
    }

    // Check if mobile number is valid
    console.log("✅ Validating mobile number:", mobileNumber);
    if (!mobileNumber || !/^[6-9]\d{9}$/.test(mobileNumber)) {
      console.log("❌ Invalid mobile number");
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return;
    }
    console.log("✅ Mobile number validation passed");

    console.log("🚀 Starting post-validation processing...");

    // If we have place objects, use their coordinates for more accurate routing
    let fromLocation = pickup;
    let toLocation = dropoff;

    console.log("📍 Initial locations:", {
      fromLocation,
      toLocation,
      pickup,
      dropoff,
    });

    if (pickupPlace?.location) {
      fromLocation = `${pickupPlace.location.lat},${pickupPlace.location.lng}`;
      console.log("📍 Updated fromLocation with coordinates:", fromLocation);
    }

    if (dropoffPlace?.location) {
      toLocation = `${dropoffPlace.location.lat},${dropoffPlace.location.lng}`;
      console.log("📍 Updated toLocation with coordinates:", toLocation);
    }

    try {
      console.log("🔧 Setting loading state...");
      setIsLoading(true);

      // Calculate distance - skip for rental and airport services
      console.log("📏 Starting distance calculation...");
      let distance = 0;

      if (journeyType === "local") {
        console.log(
          "📏 Local service detected - skipping distance calculation",
        );
        // For local services (rental/airport), we don't need distance calculation
        distance = 0; // Set to 0 since it's not relevant for local services
      } else {
        // Only calculate distance for outstation journeys
        console.log("📏 Outstation journey - calculating distance");

        // First try to get known distance
        console.log("📏 Checking for known distance...");
        const normalizedFrom = normalizeCityName(pickup);
        const normalizedTo = normalizeCityName(dropoff);
        console.log("📏 Normalized cities:", { normalizedFrom, normalizedTo });
        const knownDistance = getKnownDistance(normalizedFrom, normalizedTo);

        if (knownDistance) {
          console.log("📏 Using known distance:", knownDistance);
          distance = knownDistance;
        } else if (calculateDistance) {
          console.log("📏 Attempting Google Maps distance calculation...");
          // If no known distance, try to calculate using Google Maps API
          try {
            const calculatedDistance = await calculateDistance(pickup, dropoff);
            if (calculatedDistance) {
              console.log(
                "📏 Google Maps distance calculated:",
                calculatedDistance,
              );
              distance = calculatedDistance;
            }
          } catch (error) {
            console.error("📏 Error calculating distance:", error);
            // Fallback to default distance if calculation fails
            distance = 50;
            console.log("📏 Using fallback distance:", distance);
          }
        } else {
          console.log(
            "📍 No distance calculation method available, using default",
          );
          // Fallback to default distance if no calculation method is available
          distance = 50;
        }
      }

      console.log("📏 Final distance:", distance);

      // Prepare booking data
      console.log("📝 Preparing booking data...");
      const bookingData = {
        pickup,
        dropoff,
        distance,
        journeyType,
        tripType,
        localServiceType: journeyType === "local" ? localServiceType : null,
        pickupDate: pickupDate.toISOString(),
        pickupTime: convertTo24Hour(pickupTime),
        mobileNumber,
        returnDate: tripType === "roundTrip" ? returnDate.toISOString() : null,
        returnTime:
          tripType === "roundTrip" ? convertTo24Hour(returnTime) : null,
        additionalCities: multiCity ? additionalCities : [],
        fromLocation,
        toLocation,
        // Local Rental specific fields
        localCity:
          journeyType === "local" && localServiceType === "rental"
            ? localCity
            : null,
        localCityName:
          journeyType === "local" && localServiceType === "rental"
            ? localCity // Use the city name directly since we're storing city names, not IDs
            : null,
        hourlyPackage:
          journeyType === "local" && localServiceType === "rental"
            ? hourlyPackage
            : null,
        hourlyPackageDetails:
          journeyType === "local" && localServiceType === "rental"
            ? rentalPackages.find((pkg) => pkg._id === hourlyPackage)
            : null,
        // Airport Transfer specific fields
        selectedAirport:
          journeyType === "local" && localServiceType === "airport"
            ? selectedAirport
            : null,
        selectedAirportDetails:
          journeyType === "local" && localServiceType === "airport"
            ? airports.find((airport) => airport.code === selectedAirport)
            : null,
        transferDirection:
          journeyType === "local" && localServiceType === "airport"
            ? transferDirection
            : null,
        airportCity:
          journeyType === "local" && localServiceType === "airport"
            ? airportCity
            : null,
      };

      console.log("Submitting booking with data:", bookingData);

      // Save to Redux store
      dispatch(updateBookingForm(bookingData));

      // Build query parameters based on journey type
      let queryParams = "";

      if (journeyType === "outstation") {
        queryParams = `?from=${encodeURIComponent(pickup)}&to=${encodeURIComponent(dropoff)}`;
        queryParams += `&fromLocation=${encodeURIComponent(fromLocation)}`;
        queryParams += `&toLocation=${encodeURIComponent(toLocation)}`;
        queryParams += `&distance=${distance}`;
        queryParams += `&journeyType=${journeyType}`;
        queryParams += `&tripType=${tripType}`;
        queryParams += `&service=outstation`;

        if (multiCity && additionalCities.length > 0) {
          const viaCities = additionalCities
            .map((city) => city.value)
            .join(",");
          queryParams += `&via=${encodeURIComponent(viaCities)}`;
        }
      } else if (journeyType === "local") {
        if (localServiceType === "rental") {
          const cityName = localCity; // Use city name directly
          const packageDetails = rentalPackages.find(
            (pkg) => pkg._id === hourlyPackage,
          );
          queryParams = `?service=rental&city=${encodeURIComponent(cityName)}&package=${encodeURIComponent(packageDetails?.name || hourlyPackage)}`;
        } else if (localServiceType === "airport") {
          const airportDetails = airports.find(
            (airport) => airport.code === selectedAirport,
          );
          const airportLocation = airportDetails
            ? airportDetails.name
            : `${selectedAirport} - ${airportCity}`;
          if (transferDirection === "to") {
            const pickupCityName =
              airportServiceCities.find((city) => city._id === pickup)?.name ||
              pickup;
            queryParams = `?service=airport&from=${encodeURIComponent(pickupCityName)}&to=${encodeURIComponent(airportLocation)}&direction=to`;
          } else {
            const dropCityName =
              airportServiceCities.find((city) => city._id === dropoff)?.name ||
              dropoff;
            queryParams = `?service=airport&from=${encodeURIComponent(airportLocation)}&to=${encodeURIComponent(dropCityName)}&direction=from`;
          }
          queryParams += `&airport=${encodeURIComponent(selectedAirport)}`;
        }
      }

      queryParams += `&date=${pickupDate.toISOString()}`;
      queryParams += `&time=${encodeURIComponent(pickupTime)}`;

      // Add trip type to URL parameters
      queryParams += `&tripType=${tripType}`;

      if (journeyType === "outstation" && tripType === "roundTrip") {
        queryParams += `&returnDate=${returnDate.toISOString()}`;
        queryParams += `&returnTime=${encodeURIComponent(returnTime)}`;
      }

      // Navigate to cab selection with query parameters
      console.log("🚀 Navigating to /cabs with params:", queryParams);
      console.log("🚀 Full navigation URL:", `/cabs${queryParams}`);

      try {
        navigate(`/cabs${queryParams}`);
        console.log("✅ Navigation successful");
      } catch (navError) {
        console.error("❌ Navigation failed:", navError);
        toast.error("Navigation failed. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error processing booking:", error);
      toast.error("Failed to process booking. Please try again.");
    } finally {
      console.log("🏁 Form submission completed");
      setIsLoading(false);
    }
  };

  // Alias for form submission
  const handleFormSubmit = handleSubmit;

  // Helper function to try API endpoints with fallback
  const tryApiRequest = async (
    endpoint,
    data,
    description = "request",
    method = "POST",
  ) => {
    const endpoints = [
      `${API_URL}${endpoint}`,
      `${FALLBACK_API_URL}${endpoint}`,
    ];

    for (let i = 0; i < endpoints.length; i++) {
      const url = endpoints[i];
      console.log(`Trying ${description} - Attempt ${i + 1}:`, url);

      try {
        let response;
        const config = {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        };

        if (method.toUpperCase() === "GET") {
          response = await axios.get(url, config);
        } else if (method.toUpperCase() === "POST") {
          response = await axios.post(url, data, config);
        } else if (method.toUpperCase() === "PUT") {
          response = await axios.put(url, data, config);
        } else if (method.toUpperCase() === "DELETE") {
          response = await axios.delete(url, config);
        }

        console.log(`${description} response from ${url}:`, response.data);
        return response;
      } catch (error) {
        console.error(
          `${description} failed at ${url}:`,
          error.message || error,
        );

        // If this is the last endpoint, throw the error
        if (i === endpoints.length - 1) {
          throw error;
        }

        // Otherwise, continue to next endpoint
        console.log(`Trying next endpoint...`);
      }
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      if (
        !contactForm.name ||
        !contactForm.email ||
        !contactForm.phone ||
        !contactForm.message
      ) {
        toast.error("All fields are required");
        return;
      }

      if (!contactHumanVerification) {
        toast.error("Please verify that you are human");
        return;
      }

      console.log("Contact form data:", contactForm);

      const response = await tryApiRequest(
        "/contact",
        contactForm,
        "Contact form submission",
      );

      if (response.data.success) {
        toast.success(
          "Message sent successfully! We will get back to you soon.",
        );
        setContactForm({ name: "", email: "", phone: "", message: "" });
        setContactHumanVerification(false);
      } else {
        toast.error(response.data.message || "Failed to send message");
      }
    } catch (error) {
      console.error("Contact form error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/contact`,
      });

      if (error.response?.status === 404) {
        toast.error(
          "API endpoint not found. Please check backend configuration.",
        );
      } else if (error.code === "ECONNABORTED") {
        toast.error("Request timeout. Please check your internet connection.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } finally {
      setContactLoading(false);
    }
  };

  // Handle business form submission
  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setBusinessLoading(true);

    try {
      if (
        !businessForm.companyName ||
        !businessForm.name ||
        !businessForm.email ||
        !businessForm.phone
      ) {
        toast.error("All fields are required");
        return;
      }

      if (!businessHumanVerification) {
        toast.error("Please verify that you are human");
        return;
      }

      console.log("Business form data:", businessForm);

      const response = await tryApiRequest(
        "/business-contact",
        businessForm,
        "Business form submission",
      );

      if (response.data.success) {
        toast.success(
          "Business inquiry sent successfully! We will contact you soon.",
        );
        setBusinessForm({ companyName: "", name: "", email: "", phone: "" });
        setBusinessHumanVerification(false);
      } else {
        toast.error(response.data.message || "Failed to send business inquiry");
      }
    } catch (error) {
      console.error("Business form error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/business-contact`,
      });

      if (error.response?.status === 404) {
        toast.error(
          "API endpoint not found. Please check backend configuration.",
        );
      } else if (error.code === "ECONNABORTED") {
        toast.error("Request timeout. Please check your internet connection.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to send business inquiry. Please try again.");
      }
    } finally {
      setBusinessLoading(false);
    }
  };

  // Handle contact form input changes
  const handleContactChange = (e) => {
    setContactForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Handle business form input changes
  const handleBusinessChange = (e) => {
    setBusinessForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span
          key={i}
          className={i <= rating ? "text-yellow-500" : "text-gray-300"}
        >
          ★
        </span>,
      );
    }
    return stars;
  };

  return (
    <div className="page-container overflow-x-hidden w-full">
      {/* Hero Section */}
      <section className="pt-16 sm:pt-18 md:pt-20 pb-1 sm:pb-2 md:pb-2 bg-gray-50 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${cabBgImage})`,
            opacity: "0.8",
            zIndex: 0,
            minHeight: "600px",
            backgroundPosition: "center center",
          }}
        ></div>

        <div className="container relative z-10 min-h-[500px] sm:min-h-[600px] md:min-h-[650px] lg:min-h-[700px]">
          <div className="flex flex-col items-center sm:items-start">
            <div
              id="booking-form"
              className="bg-white rounded-3xl shadow-lg overflow-hidden w-[95%] sm:w-[90%] md:w-[420px] lg:w-[460px] xl:w-[480px] mx-auto sm:mx-0 sm:ml-4 md:ml-6 lg:ml-8 xl:ml-12 mb-4 sm:mb-8 md:mb-10 lg:mb-12 flex flex-col relative z-10"
              style={{
                boxShadow:
                  "rgba(0, 0, 0, 0.12) 0px 1px 3px 0px, rgba(0, 0, 0, 0.24) 0px 1px 2px 0px",
              }}
            >
              {/* Yellow header section */}
              <div className="bg-gradient-to-r from-yellow-400 to-orange-400 px-6 py-4">
                <h1 className="text-xl font-bold text-black text-center">
                  All India Cab Service
                </h1>
              </div>
              {/* Main Tabs */}
              <div className="flex gap-2 p-4 bg-gray-50">
                <button
                  className={`flex-1 py-3 px-4 text-sm font-medium text-center rounded-lg transition-all duration-200 ${journeyType === "outstation" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-md" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
                  onClick={() => setJourneyType("outstation")}
                >
                  Outstation
                </button>
                <button
                  className={`flex-1 py-3 px-4 text-sm font-medium text-center rounded-lg transition-all duration-200 ${journeyType === "local" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-md" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
                  onClick={() => setJourneyType("local")}
                >
                  Local / Airport
                </button>
              </div>

              {/* Sub Tabs - Show for both Outstation and Local */}
              {journeyType === "outstation" && (
                <div className="flex gap-2 px-4 pb-4 bg-gray-50">
                  <button
                    className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${tripType === "oneWay" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"}`}
                    onClick={() => setTripType("oneWay")}
                  >
                    One Way
                  </button>
                  <button
                    className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${tripType === "roundTrip" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"}`}
                    onClick={() => setTripType("roundTrip")}
                  >
                    Round Trip
                  </button>
                </div>
              )}

              {/* Sub Tabs for Local / Airport */}
              {journeyType === "local" && (
                <div className="flex gap-2 px-4 pb-4 bg-gray-50">
                  <button
                    className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${localServiceType === "rental" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"}`}
                    onClick={() => setLocalServiceType("rental")}
                  >
                    Local Rental
                  </button>
                  <button
                    className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-lg transition-all duration-200 ${localServiceType === "airport" ? "bg-gradient-to-r from-yellow-400 to-orange-400 text-black shadow-sm" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"}`}
                    onClick={() => setLocalServiceType("airport")}
                  >
                    Airport Transfer
                  </button>
                </div>
              )}

              {/* Booking Form */}
              <form
                onSubmit={handleSubmit}
                className="p-4 text-gray-800 bg-white flex-grow"
              >
                <div className="space-y-1">
                  {/* Conditional form fields based on journey type */}
                  {journeyType === "outstation" ? (
                    <>
                      {/* Outstation Pickup Location */}
                      <LocationInput
                        label="Pickup Location"
                        value={pickup}
                        onChange={(value) => {
                          setPickup(value);
                          if (!value) setPickupPlace(null);
                        }}
                        onPlaceSelected={(place) => {
                          if (place) {
                            setPickupPlace(place);
                            setPickup(place.formatted_address);
                          }
                        }}
                        placeholder="Enter pickup location"
                        required
                      />

                      {/* Outstation Dropoff Location */}
                      <LocationInput
                        label="Destination Location"
                        value={dropoff}
                        onChange={(value) => {
                          setDropoff(value);
                          if (!value) setDropoffPlace(null);
                        }}
                        onPlaceSelected={(place) => {
                          if (place) {
                            setDropoffPlace(place);
                            setDropoff(place.formatted_address);
                          }
                        }}
                        placeholder="Enter destination location"
                        required
                      />
                    </>
                  ) : (
                    <>
                      {/* Local Service Fields */}
                      {localServiceType === "rental" ? (
                        <>
                          {/* City Selector for Local Rental */}
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Select City for Local Rental
                            </label>
                            <div className="relative">
                              <select
                                value={localCity}
                                onChange={(e) => {
                                  setLocalCity(e.target.value);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                required
                              >
                                <option value="">
                                  {rentalPackages.length === 0
                                    ? "Loading cities..."
                                    : "Choose a city"}
                                </option>
                                {/* Get unique cities from rental packages */}
                                {[
                                  ...new Set(
                                    rentalPackages
                                      .filter(
                                        (pkg) =>
                                          pkg.serviceCities &&
                                          pkg.serviceCities.length > 0,
                                      )
                                      .flatMap((pkg) =>
                                        pkg.serviceCities.map(
                                          (sc) => sc.cityName || sc.name || sc,
                                        ),
                                      )
                                      .filter(Boolean),
                                  ),
                                ].map((cityName) => (
                                  <option key={cityName} value={cityName}>
                                    {cityName}
                                  </option>
                                ))}
                                {/* Fallback options if no rental packages loaded */}
                                {rentalPackages.length === 0 && (
                                  <>
                                    <option value="Delhi">Delhi</option>
                                    <option value="Mumbai">Mumbai</option>
                                    <option value="Bangalore">Bangalore</option>
                                    <option value="Chennai">Chennai</option>
                                    <option value="Kolkata">Kolkata</option>
                                  </>
                                )}
                              </select>
                            </div>
                          </div>

                          {/* Hourly Package Selector */}
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Select Package
                            </label>
                            <div className="relative">
                              <select
                                value={hourlyPackage}
                                onChange={(e) =>
                                  setHourlyPackage(e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                required
                              >
                                <option value="">
                                  {rentalPackages.length === 0
                                    ? "Loading packages..."
                                    : "Choose a package"}
                                </option>
                                {rentalPackages
                                  .filter((pkg) => {
                                    // Filter packages that are available in the selected city
                                    if (!localCity) return true; // Show all if no city selected

                                    // Check if package has serviceCities
                                    if (!pkg.serviceCities || pkg.serviceCities.length === 0) return false;

                                    // Check if selected city is in the package's service cities
                                    return pkg.serviceCities.some(
                                      serviceCity => serviceCity.cityName === localCity
                                    );
                                  })
                                  .map((pkg) => (
                                    <option key={pkg._id} value={pkg._id}>
                                      {pkg.name} - {pkg.hours} Hours /{" "}
                                      {pkg.kilometers} KM
                                    </option>
                                  ))}
                                {/* Show message when no packages available for selected city */}
                                {localCity && rentalPackages.length > 0 &&
                                  rentalPackages.filter((pkg) => {
                                    if (!pkg.serviceCities || pkg.serviceCities.length === 0) return false;
                                    return pkg.serviceCities.some(
                                      serviceCity => serviceCity.cityName === localCity
                                    );
                                  }).length === 0 && (
                                  <option value="" disabled>
                                    No packages available for {localCity}
                                  </option>
                                )}
                                {/* Fallback options if no rental packages loaded */}
                                {rentalPackages.length === 0 && (
                                  <>
                                    <option value="2h/20km">
                                      2 Hours / 20 KM
                                    </option>
                                    <option value="4h/40km">
                                      4 Hours / 40 KM
                                    </option>
                                    <option value="8h/80km">
                                      8 Hours / 80 KM
                                    </option>
                                    <option value="12h/120km">
                                      12 Hours / 120 KM
                                    </option>
                                  </>
                                )}
                              </select>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Airport Transfer Fields */}
                          {/* Transfer Direction Toggle */}
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Transfer Direction
                            </label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${transferDirection === "to" ? "bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"}`}
                                onClick={() => setTransferDirection("to")}
                              >
                                <FaPlane className="mr-1" />
                                To Airport
                              </button>
                              <button
                                type="button"
                                className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${transferDirection === "from" ? "bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200"}`}
                                onClick={() => setTransferDirection("from")}
                              >
                                <FaPlane className="mr-1" />
                                From Airport
                              </button>
                            </div>
                          </div>

                          {/* Airport Selector */}
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              Select Airport
                            </label>
                            <div className="relative">
                              <select
                                value={selectedAirport}
                                onChange={(e) => {
                                  setSelectedAirport(e.target.value);
                                  const airport = airports.find(
                                    (a) => a.code === e.target.value,
                                  );
                                  if (airport) {
                                    setAirportCity(airport.city?.name || "");
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                required
                              >
                                <option value="">Choose an airport</option>
                                {airports.map((airport) => (
                                  <option
                                    key={airport.code}
                                    value={airport.code}
                                  >
                                    {airport.name} - {airport.city?.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* City Selector based on airport service cities */}
                          <div>
                            <label className="block text-sm font-medium mb-1">
                              {transferDirection === "to"
                                ? "Pickup City"
                                : "Drop City"}
                            </label>
                            <div className="relative">
                              <select
                                value={
                                  transferDirection === "to" ? pickup : dropoff
                                }
                                onChange={(e) => {
                                  const selectedCity =
                                    airportServiceCities.find(
                                      (city) => city._id === e.target.value,
                                    );
                                  if (transferDirection === "to") {
                                    setPickup(
                                      selectedCity ? selectedCity.name : "",
                                    );
                                    setPickupPlace(selectedCity);
                                  } else {
                                    setDropoff(
                                      selectedCity ? selectedCity.name : "",
                                    );
                                    setDropoffPlace(selectedCity);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                required
                              >
                                <option value="">
                                  {selectedAirport
                                    ? `Choose ${transferDirection === "to" ? "pickup" : "drop"} city`
                                    : "Select airport first"}
                                </option>
                                {airportServiceCities.map((city) => (
                                  <option key={city._id} value={city._id}>
                                    {city.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Add More City Button - Only for outstation journeys */}
                  {journeyType === "outstation" && (
                    <div>
                      <button
                        type="button"
                        className="text-primary hover:text-primary-dark flex items-center text-sm font-medium transition-transform duration-200 hover:scale-105"
                        onClick={() => {
                          // Add a new city input field
                          const newCityId = cityCounter + 1;
                          setAdditionalCities([
                            ...additionalCities,
                            { id: newCityId, value: "" },
                          ]);
                          setCityCounter(newCityId);
                          setMultiCity(true);
                        }}
                      >
                        <FaPlus className="mr-1.5" /> Add More City
                      </button>
                    </div>
                  )}

                  {/* Additional City Fields - Only for outstation journeys */}
                  {journeyType === "outstation" &&
                    additionalCities.length > 0 && (
                      <div className="space-y-1 mt-1">
                        {additionalCities.map((city, index) => (
                          <div key={city.id} className="relative group">
                            <div className="flex items-center">
                              <div className="relative flex-grow">
                                <LocationInput
                                  label={`Via City ${index + 1}`}
                                  value={city.value}
                                  onChange={(newValue) => {
                                    const updatedCities = [...additionalCities];
                                    updatedCities[index].value = newValue;
                                    setAdditionalCities(updatedCities);
                                  }}
                                  placeholder="Enter via city"
                                />
                              </div>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  const updatedCities = additionalCities.filter(
                                    (_, i) => i !== index,
                                  );
                                  setAdditionalCities(updatedCities);
                                  if (updatedCities.length === 0) {
                                    setMultiCity(false);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Date and Time Section */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                    {/* Date Picker */}
                    <div className="flex-1">
                      <div className="relative">
                        <label className="block text-sm font-medium mb-1">
                          {journeyType === "local" &&
                          localServiceType === "rental"
                            ? "Rental Date"
                            : journeyType === "local" &&
                                localServiceType === "airport"
                              ? transferDirection === "to"
                                ? "Pickup Date"
                                : "Landing Date"
                              : "Pickup Date"}
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={pickupDate}
                            onChange={(date) => setPickupDate(date)}
                            minDate={new Date()}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            dateFormat="dd/MM/yyyy"
                          />
                          <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                      </div>
                    </div>

                    {/* Time Picker */}
                    <div className="flex-1">
                      <div className="relative">
                        <label className="block text-sm font-medium mb-1">
                          {journeyType === "local" &&
                          localServiceType === "rental"
                            ? "Start Time"
                            : journeyType === "local" &&
                                localServiceType === "airport"
                              ? transferDirection === "to"
                                ? "Pickup Time"
                                : "Landing Time"
                              : "Pickup Time"}
                        </label>
                        <div className="relative">
                          <div className="flex items-center space-x-2 bg-white p-2 rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 flex-1">
                            <FaClock className="text-gray-500 ml-1" />
                            <input
                              type="time"
                              value={convertTo24Hour(pickupTime)}
                              onChange={handlePickupTimeChange}
                              min={
                                isToday(pickupDate) ? getCurrentTime() : "00:00"
                              }
                              className="flex-1 outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Return Date/Time (only for Outstation Round Trip) */}
                  {journeyType === "outstation" && tripType === "roundTrip" && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                      {/* Return Date Picker */}
                      <div className="flex-1">
                        <div className="relative">
                          <label className="block text-sm font-medium mb-1">
                            Return Date
                          </label>
                          <div className="relative">
                            <DatePicker
                              selected={returnDate}
                              onChange={(date) => setReturnDate(date)}
                              minDate={pickupDate || new Date()}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                              dateFormat="dd/MM/yyyy"
                            />
                            <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      {/* Return Time Picker */}
                      <div className="flex-1">
                        <div className="relative">
                          <label className="block text-sm font-medium mb-1">
                            Return Time
                          </label>
                          <div className="relative">
                            <select
                              value={returnTime}
                              onChange={(e) => setReturnTime(e.target.value)}
                              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                            >
                              {Array.from({ length: 24 }, (_, i) => {
                                let hour = i;
                                const ampm = hour >= 12 ? "PM" : "AM";
                                hour = hour % 12;
                                hour = hour ? hour : 12; // the hour '0' should be '12'
                                const displayHour = hour.toString();
                                const value = `${displayHour}:00 ${ampm}`;
                                return (
                                  <option key={value} value={value}>
                                    {value}
                                  </option>
                                );
                              })}
                            </select>
                            <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mobile Number */}
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Mobile Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        placeholder="Enter your mobile number"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                        maxLength="10"
                        pattern="[0-9]{10}"
                      />
                      <FaPhoneAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="text-red-500 text-sm mb-2 p-2 bg-red-50 rounded-md border border-red-100">
                      {errorMessage}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 text-black rounded-lg py-3 px-5 font-medium hover:from-yellow-500 hover:to-orange-500 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    <FaTaxi className="mr-2" />
                    Search Cabs
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <section className="pt-8 pb-16 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Why Choose Us
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Verified Cabs */}
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-blue-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <FaTaxi className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Verified Cabs
                </h3>
                <p className="text-gray-600 text-center">
                  All cabs are regularly inspected to ensure your comfort and
                  safety.
                </p>
              </div>

              {/* Safe Travel */}
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-green-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <FaShieldAlt className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Safe Travel
                </h3>
                <p className="text-gray-600 text-center">
                  Your safety is our priority with GPS tracking and 24/7
                  customer support.
                </p>
              </div>

              {/* Professional Drivers */}
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-purple-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <FaUserTie className="text-purple-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Professional Drivers
                </h3>
                <p className="text-gray-600 text-center">
                  Experienced, verified drivers who know the best routes.
                </p>
              </div>

              {/* Clear Pricing */}
              <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-amber-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <FaRupeeSign className="text-amber-600 text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-center mb-3">
                  Clear Pricing
                </h3>
                <p className="text-gray-600 text-center">
                  Transparent fare estimates with no hidden charges.
                </p>
              </div>
            </div>
          </div>
        </section>
      </section>

      {/* Popular Destinations */}
      <section className="pt-6 pb-12 sm:pb-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-8">
            Explore Top Experiences
          </h2>

          <Slider {...citySettings} className="experience-slider">
            {experiences.map((experience, index) => (
              <div key={index} className="px-2">
                <div className="bg-white rounded-xl shadow-md overflow-hidden h-64 sm:h-80 relative group">
                  <img
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src =
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-4">
                    <h3 className="text-white text-lg font-semibold">
                      {experience.title}
                    </h3>
                    <p className="text-white text-opacity-80 text-sm mt-1">
                      {experience.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>
      {/*why choose us section */}

      {/* CabBazar Taxi Services */}
      <section
        className="pt-8 pb-16 bg-gray-50 relative overflow-hidden"
        id="services"
      >
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-br from-blue-50 to-purple-50 -z-10"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="order-1">
              <div className="text-center lg:text-left mb-12">
                <span className="text-blue-600 font-semibold tracking-wider uppercase text-sm">
                  Our Services
                </span>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
                  UrbanRide Taxi Services
                </h2>
                <div className="h-1.5 w-24 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full lg:mx-0 mx-auto"></div>
              </div>

              {/* Mobile Image - Only shows on small screens */}
              <div className="lg:hidden relative mb-10 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={car1}
                  alt="Premium Taxi Services"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Outstation Cabs */}
                <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-100 transform hover:-translate-y-1">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl text-white mr-4 group-hover:scale-110 transition-transform duration-300">
                      <FaRoute className="text-2xl" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                      Outstation Cabs
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "Round Trip cab services",
                      "Multiple Destinations as you plan",
                      "All sightseeing included",
                      "No driver boarding/lodging",
                      "Simple all-inclusive pricing",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="bg-blue-50 p-1 rounded-full mr-3 mt-1 group-hover/item:bg-blue-100 transition-colors">
                          <FaCheckCircle className="text-blue-500 text-sm" />
                        </div>
                        <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setJourneyType("outstation");
                        setTripType("roundTrip");
                        document
                          .getElementById("booking-form")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                      }}
                      className="text-blue-600 font-medium hover:text-blue-700 flex items-center group"
                    >
                      Book Now
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </button>
                  </div>
                </div>

                {/* One Way Cab */}
                <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-green-100 transform hover:-translate-y-1">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl text-white mr-4 group-hover:scale-110 transition-transform duration-300">
                      <FaArrowRight className="text-2xl" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-transparent">
                      One Way Cab
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "No return fare - Pay one way only",
                      "One pick-up, One drop, One-way fare",
                      "No hidden charges - All inclusive",
                      "Best prices on popular routes",
                      "Toll tax & state tax included",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="bg-green-50 p-1 rounded-full mr-3 mt-1 group-hover/item:bg-green-100 transition-colors">
                          <FaCheckCircle className="text-green-500 text-sm" />
                        </div>
                        <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setJourneyType("outstation");
                        setTripType("oneWay");
                        document
                          .getElementById("booking-form")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                      }}
                      className="text-green-600 font-medium hover:text-green-700 flex items-center group"
                    >
                      Book Now
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </button>
                  </div>
                </div>

                {/* Local Hourly Car Rental */}
                <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-100 transform hover:-translate-y-1">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl text-white mr-4 group-hover:scale-110 transition-transform duration-300">
                      <FaClock className="text-2xl" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">
                      Local Car Rental
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "Flexible hourly/daily rentals",
                      "Perfect for local sightseeing",
                      "Business & corporate travel",
                      "Wedding & special events",
                      "Shopping & personal use",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="bg-purple-50 p-1 rounded-full mr-3 mt-1 group-hover/item:bg-purple-100 transition-colors">
                          <FaCheckCircle className="text-purple-500 text-sm" />
                        </div>
                        <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setJourneyType("local");
                        setLocalServiceType("rental");
                        document
                          .getElementById("booking-form")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                      }}
                      className="text-purple-600 font-medium hover:text-purple-700 flex items-center group"
                    >
                      Book Now
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </button>
                  </div>
                </div>

                {/* Airport Taxi */}
                <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-amber-100 transform hover:-translate-y-1">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-xl text-white mr-4 group-hover:scale-110 transition-transform duration-300">
                      <FaPlane className="text-2xl" />
                    </div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-400 bg-clip-text text-transparent">
                      Airport Taxi
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {[
                      "24/7 airport transfer service",
                      "From/To any location",
                      "Flight monitoring for delays",
                      "Assured luggage space",
                      "Guaranteed on-time pickups",
                    ].map((item, index) => (
                      <li key={index} className="flex items-start group/item">
                        <div className="bg-amber-50 p-1 rounded-full mr-3 mt-1 group-hover/item:bg-amber-100 transition-colors">
                          <FaCheckCircle className="text-amber-500 text-sm" />
                        </div>
                        <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setJourneyType("local");
                        setLocalServiceType("airport");
                        document
                          .getElementById("booking-form")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                      }}
                      className="text-amber-600 font-medium hover:text-amber-700 flex items-center group"
                    >
                      Book Now
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Gallery - Right Side */}
            <div className="relative order-2 h-full min-h-[600px] w-full hidden lg:block">
              {/* Main Image */}
              <div className="absolute top-0 right-0 w-4/5 h-4/5 rounded-2xl overflow-hidden shadow-2xl z-10 transform rotate-3">
                <img
                  src={car2}
                  alt="Premium Taxi Services"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* Secondary Image */}
              <div className="absolute bottom-0 left-0 w-3/5 h-3/5 rounded-2xl overflow-hidden shadow-2xl z-20 transform -rotate-6">
                <img
                  src={car1}
                  alt="Luxury Car Interior"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-6 right-1/4 bg-white px-6 py-3 rounded-full shadow-lg z-30 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                <span className="font-medium text-gray-800">
                  24/7 Available
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Popular Cab Routes */}
      <section className="py-16 bg-white" id="popular-routes">
        <div className="container mx-auto px-4 ">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Popular Cab Routes
            </h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Slider */}
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-xl">
              <Slider
                dots={true}
                infinite={true}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                autoplay={true}
                autoplaySpeed={3000}
                className="h-full"
              >
                {[
                  {
                    id: 1,
                    title: "Bangalore to Ooty",
                    from: "Bangalore",
                    to: "Ooty",
                    image: banglor,
                    distance: "269 km",
                    duration: "6 hours",
                    price: 3195,
                  },
                  {
                    id: 2,
                    title: "Mumbai to Shirdi",
                    from: "Mumbai",
                    to: "Shirdi",
                    image: shimla,
                    distance: "258 km",
                    duration: "5 hours",
                    price: 3063,
                  },
                  {
                    id: 3,
                    title: "Delhi to Manali",
                    from: "Delhi",
                    to: "Manali",
                    image: delhi,
                    distance: "496 km",
                    duration: "12 hours",
                    price: 5890,
                  },
                  {
                    id: 4,
                    title: "Delhi to Haridwar",
                    from: "Delhi",
                    to: "Haridwar",
                    image: haridwar,
                    distance: "240 km",
                    duration: "5 hours",
                    price: 2849,
                  },
                ].map((route) => (
                  <div key={route.id} className="relative h-[500px] w-full">
                    <div
                      className="relative w-full h-full cursor-pointer group transition-transform duration-300 hover:scale-105"
                      onClick={() => {
                        navigate(`/cabs?from=${encodeURIComponent(route.from)}&to=${encodeURIComponent(route.to)}`);
                      }}
                    >
                      <img
                        src={route.image}
                        alt={route.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6 group-hover:from-black/80 transition-colors duration-300">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-yellow-300 transition-colors duration-300">
                            {route.title}
                          </h3>
                          <div className="flex items-center text-white/80 text-sm space-x-4 group-hover:text-white transition-colors duration-300">
                            <span>{route.distance}</span>
                            <span>•</span>
                            <span>{route.duration}</span>
                            <span>•</span>
                            <span>Starts from ₹{route.price}</span>
                          </div>
                          <div className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-block">
                            Click to Book Now
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>

            {/* Route Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  id: 1,
                  from: "Bangalore",
                  to: "Ooty",
                  distance: "269 km",
                  duration: "6 hours",
                  price: 3195,
                  features: [
                    "Scenic mountain route",
                    "Comfortable AC cabs",
                    "Experienced drivers",
                    "24/7 customer support",
                  ],
                },
                {
                  id: 2,
                  from: "Mumbai",
                  to: "Shirdi",
                  distance: "258 km",
                  duration: "5 hours",
                  price: 3063,
                  features: [
                    "Direct route available",
                    "Multiple cab options",
                    "Flexible timings",
                    "Safe journey guarantee",
                  ],
                },
                {
                  id: 3,
                  from: "Delhi",
                  to: "Manali",
                  distance: "496 km",
                  duration: "12 hours",
                  price: 5890,
                  features: [
                    "Premium SUV options",
                    "Mountain route expertise",
                    "Comfort stops included",
                    "Luggage assistance",
                  ],
                },
                {
                  id: 4,
                  from: "Delhi",
                  to: "Haridwar",
                  distance: "240 km",
                  duration: "5 hours",
                  price: 2849,
                  features: [
                    "Religious tour packages",
                    "Clean and sanitized cars",
                    "Experienced drivers",
                    "Flexible booking options",
                  ],
                },
              ].map((route) => (
                <div
                  key={route.id}
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group border border-gray-100 hover:border-blue-200"
                  onClick={() => {
                    navigate(`/cabs?from=${encodeURIComponent(route.from)}&to=${encodeURIComponent(route.to)}`);
                  }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                        {route.from} to {route.to}
                      </h3>
                      <div className="flex items-center text-gray-600 text-sm mt-1 group-hover:text-gray-700 transition-colors duration-300">
                        <span>{route.distance}</span>
                        <span className="mx-2">•</span>
                        <span>{route.duration}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-300">
                        Starting from
                      </div>
                      <div className="text-2xl font-bold text-blue-600 group-hover:text-blue-700 transition-colors duration-300">
                        ₹{route.price}
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-2 mt-4">
                    {route.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <svg
                          className="w-4 h-4 text-green-500 mr-2 group-hover:text-green-600 transition-colors duration-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                        <span className="text-gray-700 group-hover:text-gray-800 transition-colors duration-300">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent double-click
                      navigate(`/cabs?from=${encodeURIComponent(route.from)}&to=${encodeURIComponent(route.to)}`);
                    }}
                    className="mt-6 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-md"
                  >
                    Book Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Business Travel Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content & Form */}
            <div className="relative z-10 lg:pr-8">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 tracking-tight">
                UrbanRide for Business
              </h2>
              <h3 className="text-2xl md:text-3xl font-light text-gray-800 mb-4">
                One Way Cab for Corporate Travel
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Save big on company's commute costs with UrbanRide's one-way cab
                fare for outstation travel!
              </p>

              {/* Mobile Image - Only visible on small screens */}
              <div className="lg:hidden mb-8 relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={taxi}
                  alt="Luxury Cab Interior"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                  <div className="absolute -bottom-6 right-1/4 bg-white px-6 py-3 rounded-full shadow-lg z-30 flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    <span className="font-medium text-gray-800">
                      24/7 Available
                    </span>
                  </div>
                </div>
              </div>

              <form
                onSubmit={handleBusinessSubmit}
                className="space-y-4 max-w-md"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={businessForm.companyName}
                    onChange={handleBusinessChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={businessForm.name}
                    onChange={handleBusinessChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={businessForm.email}
                    onChange={handleBusinessChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email id"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={businessForm.phone}
                    onChange={handleBusinessChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                {/* Human Verification */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="business-human-verification"
                        checked={businessHumanVerification}
                        onChange={(e) =>
                          setBusinessHumanVerification(e.target.checked)
                        }
                        className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        required
                      />
                      {businessHumanVerification && (
                        <svg
                          className="absolute top-0.5 left-0.5 w-4 h-4 text-blue-600 pointer-events-none"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="business-human-verification"
                        className="text-sm font-medium text-gray-700 select-none cursor-pointer flex items-center"
                      >
                        <span className="mr-2">🤖</span>
                        I'm not a robot
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Please verify that you are human
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-xs text-gray-400">
                      <div className="w-8 h-8 bg-blue-50 rounded border border-blue-200 flex items-center justify-center mb-1">
                        <svg
                          className="w-4 h-4 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <span>Privacy</span>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={businessLoading || !businessHumanVerification}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {businessLoading ? "Submitting..." : "Submit"}
                </button>
              </form>
            </div>

            {/* Images Section */}
            <div className="relative lg:h-[600px]">
              {/* Main Image */}
              <div className="absolute top-0 right-0 w-4/5 h-4/5 rounded-2xl overflow-hidden shadow-2xl transform rotate-3">
                <img
                  src={taxi}
                  alt="Luxury Cab Interior"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* Secondary Image */}
              <div className="absolute bottom-0 left-0 w-3/5 h-3/5 rounded-2xl overflow-hidden shadow-2xl transform -rotate-6">
                <img
                  src={taxi2}
                  alt="Professional Driver"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Meet Our Satisfied Customers
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mx-auto"></div>
            <p className="text-gray-600 mt-6 max-w-2xl mx-auto">
              Discover why thousands of customers trust UrbanRide for their
              travel needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Rahul Sharma",
                role: "Business Traveler",
                image: testi1,
                rating: 5,
                review:
                  "UrbanRide has transformed my business travel experience. The service is reliable and professional.",
                date: "2 weeks ago",
              },
              {
                name: "Priya Patel",
                role: "Frequent Traveler",
                image: testi2,
                rating: 5,
                review:
                  "The drivers are courteous and the cars are always clean. Best cab service I've used!",
                date: "1 month ago",
              },
              {
                name: "Amit Kumar",
                role: "Corporate Client",
                image: testi3,
                rating: 5,
                review:
                  "Their business solutions have helped us streamline our corporate travel management.",
                date: "3 weeks ago",
              },
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover ring-4 ring-blue-50"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                      Verified
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold text-gray-800 text-lg">
                      {testimonial.name}
                    </h3>
                    <p className="text-blue-600 text-sm font-medium">
                      {testimonial.role}
                    </p>
                    <p className="text-gray-500 text-xs">{testimonial.date}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-yellow-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-1.414 1.414L11.414 10l-4.293 4.293a1 1 0 00-1.414-1.414L10 11.414l-4.293-4.293a1 1 0 00-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {testimonial.review}
                </p>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FaThumbsUp className="text-blue-500" />
                      <span className="text-sm text-gray-500">Helpful</span>
                    </div>
                    <button className="text-blue-600 text-sm font-medium hover:text-blue-700">
                      Share Review
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-50" id="contact">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            {/* Left Info */}
            <div className="space-y-6 order-2 md:order-1">
              <h2 className="text-4xl font-extrabold text-gray-800 mb-4 tracking-tight">
                Contact UrbanRide
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Send us a message and we will get back to you as soon as
                possible!
              </p>

              <div className="space-y-6 text-gray-700">
                <div className="flex items-center space-x-3 bg-blue-50 p-3 rounded-lg shadow-sm">
                  <FaPhoneAlt className="text-blue-600 text-xl" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Call us at</h3>
                    <p className="text-blue-600 text-lg font-bold">
                      +91 7096386636
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 bg-blue-50 p-3 rounded-md shadow-md text-base">
                  <svg
                    className="text-blue-600 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      Email us at
                    </h3>
                    <p className="text-blue-600 text-sm font-bold">
                      info@rajeshwarinfotech.com
                    </p>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-800 mt-6 text-xl">
                  Connect with Us
                </h3>
                {/* Social Icons */}
                <div className="flex space-x-5 mt-4">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaFacebookF size={20} />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 hover:text-pink-800"
                  >
                    <FaInstagram size={20} />
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-600"
                  >
                    <FaTwitter size={20} />
                  </a>
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaLinkedin size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="order-1 md:order-2">
              <form
                onSubmit={handleContactSubmit}
                className="bg-white rounded-xl shadow-md p-8 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email id"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your message
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="4"
                    placeholder="Tell us your thoughts and feelings..."
                    required
                  ></textarea>
                </div>

                {/* Human Verification */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="contact-human-verification"
                        checked={contactHumanVerification}
                        onChange={(e) =>
                          setContactHumanVerification(e.target.checked)
                        }
                        className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        required
                      />
                      {contactHumanVerification && (
                        <svg
                          className="absolute top-0.5 left-0.5 w-4 h-4 text-blue-600 pointer-events-none"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="contact-human-verification"
                        className="text-sm font-medium text-gray-700 select-none cursor-pointer flex items-center"
                      >
                        <span className="mr-2">🤖</span>
                        I'm not a robot
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Please verify that you are human
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-xs text-gray-400">
                      <div className="w-8 h-8 bg-blue-50 rounded border border-blue-200 flex items-center justify-center mb-1">
                        <svg
                          className="w-4 h-4 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <span>Privacy</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={contactLoading || !contactHumanVerification}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {contactLoading ? "Submitting..." : "Submit"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
